import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import { Img, Link } from "@react-email/components";
import { useAttachmentMetadata } from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { useParseStyles } from "../../../Design/useParseStyles";
import { defaultStyles } from "./defaultStyles";
import defaultPlaceholderImage from "./img_preview_large.png";

interface ImageProps extends Omit<ComponentPropsWithRef<"img">, "loading"> {
  alt?: string;
  "data-preview-mode": boolean;
  href?: string;
  image: {
    signed_id: string;
    placeholder?: string;
  };
  loading?: "lazy" | "eager";
  styles?: Record<string, StyleValue>;
}

export const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      image,
      styles = {},
      alt = "",
      href,
      loading = "lazy",
      "data-preview-mode": isPreviewMode,
      ...rest
    },
    ref,
  ) => {
    const inlineStyles = useParseStyles({
      baseStyles: {
        display: "block",
      },
      styles,
      defaultStyles,
    });

    const attachmentMetadata = useAttachmentMetadata(image.signed_id);
    const { url } = attachmentMetadata ?? {};
    const imageSrc = url ?? image.placeholder ?? defaultPlaceholderImage;

    const ImageElement = (
      <Img
        {...rest}
        ref={ref}
        src={imageSrc}
        width={inlineStyles.width}
        alt={alt}
        style={inlineStyles}
        loading={loading}
      />
    );

    return href ? (
      <Link href={isPreviewMode ? undefined : href}>{ImageElement}</Link>
    ) : (
      ImageElement
    );
  },
);

Image.displayName = "Image";
